/**
 * O atendimento aberto.
 *
 * As ações mudam conforme o estado, e só aparecem as que fazem sentido agora:
 * um pedido mostra confirmar/recusar, um confirmado mostra iniciar/concluir/
 * faltou. Menos escolha na tela é menos erro no meio do expediente.
 */

import { formatMoneyInput, parseMoneyToCents } from '@barbearia/core';
import type {
  AppointmentMessageKind,
  AppointmentPhotoView,
  AppointmentView,
  PaymentMethod,
  PhotoMoment,
  ServiceView,
} from '@barbearia/shared';
import {
  describeServices,
  MAX_PHOTOS_PER_APPOINTMENT,
  PAYMENT_LABELS,
  PHOTO_MOMENT_LABELS,
  STATUS_LABELS,
} from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PhotoPicker } from '../../components/PhotoPicker.tsx';
import { Badge, Button, ErrorNote, OptionList, Sheet } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { duration, phone as formatPhone, minutesOfDay, money, timeOf } from '../../lib/format.ts';
import { PHOTO_RESIZE } from '../../lib/image.ts';
import { SeriesFormSheet, SeriesSheet } from './Series.tsx';

interface Props {
  appointment: AppointmentView;
  timezone: string;
  onClose: () => void;
}

const PAYMENTS: PaymentMethod[] = ['PIX', 'CASH', 'DEBIT', 'CREDIT', 'OTHER'];

/** Enquanto o atendimento não acabou, ainda dá para mudar o que será feito. */
const OPEN_STATUSES = new Set(['REQUESTED', 'CONFIRMED', 'IN_PROGRESS']);

export function AppointmentSheet({ appointment, timezone, onClose }: Props) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentMethod>('PIX');
  const [latePicker, setLatePicker] = useState(false);

  /**
   * Concluir é uma sequência, não um botão.
   *
   * A foto vem antes do dinheiro de propósito: quando a tela pergunta "quanto
   * ele pagou?", o atendimento acabou na cabeça do barbeiro e o cliente já está
   * de pé. Pedir a foto depois disso é pedir tarde.
   */
  const [closing, setClosing] = useState<'photo' | 'payment' | null>(null);

  /** A tabela dos serviços que estão neste atendimento, somada. */
  const tabledPrice = appointment.services.reduce((sum, item) => sum + item.priceCents, 0);

  /** O que vai ser cobrado de verdade, que nem sempre é a tabela. */
  const [price, setPrice] = useState(() => formatMoneyInput(appointment.priceCents ?? tabledPrice));

  /** A lista de serviços aberta para edição — "hoje ele vai querer a barba também". */
  const [editingServices, setEditingServices] = useState<string[] | null>(null);

  // As fotos vivem em estado local porque a folha recebe o atendimento por
  // propriedade, não por consulta própria. Cada rota de foto devolve o
  // atendimento inteiro, então a lista se atualiza sem recarregar a agenda.
  const [photos, setPhotos] = useState<AppointmentPhotoView[]>(appointment.photos);

  /** "Repetir" aberto (montar a série) ou a série deste atendimento aberta. */
  const [repeating, setRepeating] = useState(false);
  const [viewingSeries, setViewingSeries] = useState(false);

  /** O app acabou de oferecer a foto do antes, logo depois de começar. */
  const [beforePrompt, setBeforePrompt] = useState(false);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['agenda'] });
    await queryClient.invalidateQueries({ queryKey: ['pending-count'] });
  };

  const refresh = async () => {
    await invalidate();
    onClose();
  };

  // O catálogo só é buscado quando ele abre a edição: a folha do atendimento é
  // aberta dezenas de vezes por dia, e quase nunca para trocar serviço.
  const { data: catalog } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
    enabled: editingServices !== null,
  });

  /**
   * Trocar os serviços de um atendimento que já existe.
   *
   * Vai pela mesma rota de remarcar, com o horário de sempre: acrescentar a
   * barba estica o atendimento para a frente, e esticar é exatamente o que
   * pode invadir o próximo — por isso a resposta de conflito é tratada aqui do
   * mesmo jeito que na hora de marcar. O sistema avisa, ele decide.
   */
  const changeServices = useMutation({
    mutationFn: (input: { serviceIds: string[]; acceptConflict: boolean }) =>
      api.patch(`/appointments/${appointment.id}/reschedule`, {
        startsAt: appointment.startsAt,
        services: input.serviceIds.map((serviceId) => ({ serviceId })),
        acceptConflict: input.acceptConflict,
      }),
    onSuccess: refresh,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const changeStatus = useMutation({
    mutationFn: (status: string) => api.post(`/appointments/${appointment.id}/status`, { status }),
    onSuccess: refresh,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  /**
   * Começar o atendimento e oferecer a foto do antes.
   *
   * A folha fica aberta de propósito: fechar e pedir para o barbeiro abrir de
   * novo para fotografar é exatamente a fricção que faria ele nunca fotografar.
   * A oferta é pulável com um toque e nunca bloqueia o expediente.
   */
  const start = useMutation({
    mutationFn: () => api.post(`/appointments/${appointment.id}/status`, { status: 'IN_PROGRESS' }),
    onSuccess: async () => {
      await invalidate();
      setBeforePrompt(true);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const complete = useMutation({
    mutationFn: () =>
      api.post(`/appointments/${appointment.id}/complete`, {
        paymentMethod: payment,
        priceCents: parseMoneyToCents(price) ?? tabledPrice,
      }),
    onSuccess: refresh,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const addPhoto = useMutation({
    mutationFn: (input: { data: string; moment: PhotoMoment }) =>
      api.post<{ appointment: AppointmentView }>(`/appointments/${appointment.id}/photos`, input),
    onSuccess: (result) => setPhotos(result.appointment.photos),
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const releasePhoto = useMutation({
    mutationFn: (input: { photoId: string; releasedToClient: boolean }) =>
      api.patch<{ appointment: AppointmentView }>(
        `/appointments/${appointment.id}/photos/${input.photoId}`,
        { releasedToClient: input.releasedToClient },
      ),
    onSuccess: (result) => setPhotos(result.appointment.photos),
  });

  const removePhoto = useMutation({
    mutationFn: (photoId: string) =>
      api.delete<{ appointment: AppointmentView }>(
        `/appointments/${appointment.id}/photos/${photoId}`,
      ),
    onSuccess: (result) => setPhotos(result.appointment.photos),
  });

  const photosFull = photos.length >= MAX_PHOTOS_PER_APPOINTMENT;
  const afterPhotos = photos.filter((photo) => photo.moment === 'AFTER');

  const chargedPrice = parseMoneyToCents(price) ?? tabledPrice;

  const whatsapp = useMutation({
    mutationFn: (input: { kind: AppointmentMessageKind; minutos?: number }) =>
      api.get<{ whatsapp: { url: string } }>(
        `/appointments/${appointment.id}/whatsapp`,
        input as unknown as Record<string, string | number | undefined>,
      ),
    // Abre o WhatsApp com o texto pronto. Ele confere e envia — nada sai daqui
    // sem ele ver, que é a diferença entre a v1 manual e um robô.
    onSuccess: (data) => window.open(data.whatsapp.url, '_blank'),
  });

  const busy = changeStatus.isPending || complete.isPending || start.isPending;
  const isOpen = ['REQUESTED', 'CONFIRMED', 'IN_PROGRESS'].includes(appointment.status);

  return (
    <Sheet
      open
      onClose={onClose}
      title={appointment.client.name}
      footer={
        // Enquanto a sequência de concluir está aberta, o rodapé some: ele
        // ofereceria um segundo "Concluir" que jogaria o barbeiro de volta
        // para a foto no meio do pagamento.
        isOpen && !closing ? (
          <div className="flex flex-col gap-2 pb-1">
            {appointment.status === 'REQUESTED' && (
              <>
                <Button full loading={busy} onClick={() => changeStatus.mutate('CONFIRMED')}>
                  Confirmar horário
                </Button>
                <Button
                  variant="danger"
                  full
                  loading={busy}
                  onClick={() => changeStatus.mutate('REJECTED')}
                >
                  Recusar
                </Button>
              </>
            )}

            {appointment.status === 'CONFIRMED' && (
              <>
                <Button full loading={busy} onClick={() => start.mutate()}>
                  Começar atendimento
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    loading={busy}
                    onClick={() => setClosing('photo')}
                  >
                    Concluir
                  </Button>
                  <Button
                    variant="danger"
                    className="flex-1"
                    loading={busy}
                    onClick={() => changeStatus.mutate('NO_SHOW')}
                  >
                    Faltou
                  </Button>
                </div>
              </>
            )}

            {appointment.status === 'IN_PROGRESS' && (
              <Button full loading={busy} onClick={() => setClosing('photo')}>
                Concluir atendimento
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={statusTone(appointment.status)}>{STATUS_LABELS[appointment.status]}</Badge>
          {appointment.overlapMinutes > 0 && (
            <Badge tone="warn">Encaixe · invade {appointment.overlapMinutes}min</Badge>
          )}
          {appointment.createdByKind === 'CLIENT' && <Badge>Pedido pelo app</Badge>}
          {appointment.series && <Badge>↻ {appointment.series.label}</Badge>}
          {appointment.client.totalNoShows > 0 && (
            <Badge tone="danger">
              {appointment.client.totalNoShows}{' '}
              {appointment.client.totalNoShows === 1 ? 'falta' : 'faltas'}
            </Badge>
          )}
        </div>

        <dl className="overflow-hidden rounded-xl border border-line">
          <Row
            label="Horário"
            value={`${timeOf(appointment.startsAt, timezone)} – ${timeOf(appointment.endsAt, timezone)}`}
          />
          <Row label="Duração" value={duration(appointment.durationMinutes)} />
          <Row
            label={appointment.services.length > 1 ? 'Serviços' : 'Serviço'}
            value={describeServices(appointment.services.map((item) => item.name))}
          />
          <Row label="Valor" value={money(appointment.priceCents ?? tabledPrice)} />
          <Row label="Profissional" value={appointment.staff.name} />
          <Row label="Telefone" value={formatPhone(appointment.client.phone)} />
          {appointment.paymentMethod && (
            <Row label="Pagamento" value={PAYMENT_LABELS[appointment.paymentMethod]} />
          )}
        </dl>

        {/*
          "Ele vem toda terça": a série nasce do atendimento que está aberto,
          com o dia, a hora e os serviços dele já escolhidos. O próprio
          atendimento vira a primeira data, em vez de ganhar um gêmeo por cima.
        */}
        {appointment.series ? (
          <button
            type="button"
            onClick={() => setViewingSeries(true)}
            className="touch-target self-start px-1 text-[14px] text-accent"
          >
            Ver horário fixo
          </button>
        ) : (
          appointment.status === 'CONFIRMED' && (
            <button
              type="button"
              onClick={() => setRepeating(true)}
              className="touch-target self-start px-1 text-[14px] text-accent"
            >
              Repetir — deixar horário fixo
            </button>
          )
        )}

        {repeating && (
          <SeriesFormSheet
            clientId={appointment.client.id}
            clientName={appointment.client.name}
            fromAppointmentId={appointment.id}
            draft={{
              serviceIds: appointment.services.map((item) => item.id),
              weekdays: [weekdayIndexOf(appointment.startsAt, timezone)],
              startMinute: minutesOfDay(appointment.startsAt, timezone),
              intervalWeeks: 1,
              startDate: calendarDateOf(appointment.startsAt, timezone),
              endDate: null,
            }}
            onClose={() => setRepeating(false)}
          />
        )}
        {viewingSeries && appointment.series && (
          <SeriesSheet seriesId={appointment.series.id} onClose={() => setViewingSeries(false)} />
        )}

        {/*
          Mudar o que vai ser feito, sem desmarcar e marcar de novo. É a
          conversa da cadeira: "aproveita e faz a barba". Só faz sentido
          enquanto o atendimento ainda não acabou.
        */}
        {OPEN_STATUSES.has(appointment.status) &&
          (editingServices === null ? (
            <button
              type="button"
              onClick={() => setEditingServices(appointment.services.map((item) => item.id))}
              className="touch-target self-start px-1 text-[14px] text-accent"
            >
              Mudar os serviços
            </button>
          ) : (
            <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3">
              <p className="text-[13px] font-semibold text-ink-faint">O que vai ser feito</p>

              <OptionList
                items={catalog?.services.filter((entry) => entry.active) ?? []}
                selected={editingServices}
                getKey={(entry) => entry.id}
                onSelect={(entry) =>
                  setEditingServices((current) =>
                    (current ?? []).includes(entry.id)
                      ? (current ?? []).filter((id) => id !== entry.id)
                      : [...(current ?? []), entry.id],
                  )
                }
                renderTitle={(entry) => entry.name}
                renderMeta={(entry) => duration(entry.defaultDurationMinutes)}
              />

              <p className="text-[13px] text-ink-faint">
                O tempo é recalculado com o que cada serviço leva para{' '}
                {appointment.client.name.split(' ')[0]}.
              </p>

              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={editingServices.length === 0}
                  loading={changeServices.isPending}
                  onClick={() =>
                    changeServices.mutate({
                      serviceIds: editingServices,
                      acceptConflict: false,
                    })
                  }
                >
                  Salvar
                </Button>
                <Button variant="ghost" onClick={() => setEditingServices(null)}>
                  Cancelar
                </Button>
              </div>

              {changeServices.isError && (
                <Button
                  variant="danger"
                  full
                  onClick={() =>
                    changeServices.mutate({ serviceIds: editingServices, acceptConflict: true })
                  }
                >
                  Encaixar mesmo assim
                </Button>
              )}
            </div>
          ))}

        {appointment.notes && (
          <div className="rounded-xl border border-line bg-surface-raised px-4 py-3">
            <p className="pb-1 text-[12px] font-semibold text-ink-faint">Observação</p>
            <p className="text-[14px] text-ink">{appointment.notes}</p>
          </div>
        )}

        {/*
          Etapa 1 de concluir: o registro do resultado. Vem antes do dinheiro
          porque é o último instante em que a cabeça do barbeiro ainda está no
          corte — depois de falar em pagamento, o atendimento já acabou para
          ele e o cliente está de pé.
        */}
        {closing === 'photo' && (
          <div className="flex flex-col gap-3 rounded-xl border border-primary bg-primary/5 p-4">
            <p className="text-[15px] font-semibold text-ink">Registrar como ficou?</p>

            {afterPhotos.length > 0 && (
              <div className="scroll-touch -mx-1 flex gap-2 overflow-x-auto px-1">
                {afterPhotos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.url}
                    alt=""
                    className="h-20 w-20 shrink-0 rounded-lg object-cover"
                  />
                ))}
              </div>
            )}

            {!photosFull && (
              <PhotoPicker
                label={afterPhotos.length > 0 ? 'Mais uma' : 'Tirar foto do depois'}
                variant="primary"
                options={PHOTO_RESIZE}
                busy={addPhoto.isPending}
                onPicked={async (data) => {
                  await addPhoto.mutateAsync({ data, moment: 'AFTER' });
                }}
              />
            )}

            <Button variant="secondary" full onClick={() => setClosing('payment')}>
              {afterPhotos.length > 0
                ? 'Pronto, ir para o pagamento'
                : 'Pular e ir para o pagamento'}
            </Button>
          </div>
        )}

        {/* Etapa 2: o que ele pagou de verdade. */}
        {closing === 'payment' && (
          <div className="flex flex-col gap-3 rounded-xl border border-primary bg-primary/5 p-4">
            <p className="text-[15px] font-semibold text-ink">Como ele pagou?</p>

            <div className="flex flex-wrap gap-2">
              {PAYMENTS.map((method) => (
                <button
                  key={method}
                  type="button"
                  onClick={() => setPayment(method)}
                  className={`touch-target rounded-lg px-3 text-[14px] font-medium ${
                    payment === method
                      ? 'bg-primary text-primary-ink'
                      : 'border border-line bg-surface text-ink-soft'
                  }`}
                >
                  {PAYMENT_LABELS[method]}
                </button>
              ))}
            </div>

            {/*
              O valor da tabela é o ponto de partida, não a resposta. Desconto
              de cliente antigo, troco que não tinha, combo que virou só barba:
              o que entra no caixa é o que ele digita aqui, e é esse número que
              o faturamento vai somar.
            */}
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-ink-soft">Valor cobrado</span>
              <span className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4">
                <span className="text-[16px] text-ink-faint">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  className="w-full touch-target bg-transparent py-3 text-[16px] tabular-nums text-ink outline-none"
                />
              </span>
            </label>

            {tabledPrice !== chargedPrice && (
              <p className="text-[13px] text-ink-faint">
                Tabela: {money(tabledPrice)} — vai gravar {money(chargedPrice)}.
              </p>
            )}

            <Button full loading={complete.isPending} onClick={() => complete.mutate()}>
              Concluir por {money(chargedPrice)}
            </Button>

            <Button variant="ghost" full onClick={() => setClosing('photo')}>
              Voltar para a foto
            </Button>
          </div>
        )}

        {/*
          A oferta que aparece logo depois de começar e de concluir. É o antes e
          depois que o barbeiro já fazia no WhatsApp — só que fica guardado na
          ficha do cliente em vez de sumir na conversa.
        */}
        {beforePrompt && !photosFull && (
          <div className="flex flex-col gap-3 rounded-xl border border-primary bg-primary/5 p-4">
            <p className="text-[15px] font-semibold text-ink">Registrar como ele chegou?</p>
            <PhotoPicker
              label="Tirar foto do antes"
              variant="primary"
              options={PHOTO_RESIZE}
              busy={addPhoto.isPending}
              onPicked={async (data) => {
                await addPhoto.mutateAsync({ data, moment: 'BEFORE' });
              }}
            />
            <Button variant="ghost" full onClick={() => setBeforePrompt(false)}>
              Agora não
            </Button>
          </div>
        )}

        <section
          className={`flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3 ${
            closing ? 'hidden' : ''
          }`}
        >
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold text-ink-soft">Fotos</p>
            <span className="text-[12px] text-ink-faint">
              {photos.length}/{MAX_PHOTOS_PER_APPOINTMENT}
            </span>
          </div>

          {photos.length > 0 && (
            <div className="scroll-touch -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {photos.map((photo) => (
                <PhotoTile
                  key={photo.id}
                  photo={photo}
                  onToggleRelease={() =>
                    releasePhoto.mutate({
                      photoId: photo.id,
                      releasedToClient: photo.releasedToClientAt === null,
                    })
                  }
                  onRemove={() => removePhoto.mutate(photo.id)}
                />
              ))}
            </div>
          )}

          {photosFull ? (
            <p className="text-[12px] text-ink-faint">Cheio. Apague uma foto para guardar outra.</p>
          ) : (
            <PhotoPicker
              label={photos.length === 0 ? 'Tirar foto' : 'Mais uma foto'}
              options={PHOTO_RESIZE}
              busy={addPhoto.isPending}
              onPicked={async (data) => {
                await addPhoto.mutateAsync({ data, moment: 'OTHER' });
              }}
            />
          )}

          <p className="text-[12px] text-ink-faint">
            As fotos ficam só para você. Toque no olho para liberar uma delas ao cliente.
          </p>
        </section>

        {error && <ErrorNote>{error}</ErrorNote>}

        {/*
          Avisar o cliente é o que o barbeiro mais faz nesta tela — e é o que
          ele fazia por WhatsApp muito antes de existir app. As duas mensagens
          rápidas cobrem o que acontece de verdade no expediente: o corte
          anterior rendeu (dá para adiantar) ou estourou (melhor avisar).
        */}
        <section className="flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3">
          <p className="text-[13px] font-semibold text-ink-soft">Avisar o cliente</p>

          <Button full onClick={() => whatsapp.mutate({ kind: 'confirmation' })}>
            Mandar confirmação
          </Button>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => whatsapp.mutate({ kind: 'readyNow' })}
            >
              Já pode vir
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setLatePicker((open) => !open)}
            >
              Vou atrasar
            </Button>
          </div>

          {latePicker && (
            <div className="flex flex-col gap-2 pt-1">
              <p className="text-[13px] text-ink-soft">Quanto tempo?</p>
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20, 30].map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => {
                      whatsapp.mutate({ kind: 'runningLate', minutos: minutes });
                      setLatePicker(false);
                    }}
                    className="touch-target rounded-lg border border-line bg-surface px-4 text-[15px] font-semibold tabular-nums text-ink"
                  >
                    {minutes} min
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="flex flex-col gap-2">
          {isOpen && (
            <Button
              variant="ghost"
              full
              loading={busy}
              onClick={() => changeStatus.mutate('CANCELED_BY_SHOP')}
            >
              Cancelar atendimento
            </Button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Uma foto na folha, com as duas decisões que ela carrega.
 *
 * A liberação é por foto, e não por atendimento, porque é assim que a coisa
 * acontece: das quatro fotos, uma ficou boa. O olho aberto marca o que o
 * cliente enxerga na tela dele; o resto continua sendo material de trabalho.
 */
function PhotoTile({
  photo,
  onToggleRelease,
  onRemove,
}: {
  photo: AppointmentPhotoView;
  onToggleRelease: () => void;
  onRemove: () => void;
}) {
  const released = photo.releasedToClientAt !== null;

  return (
    <div className="relative shrink-0">
      <img src={photo.url} alt="" className="h-24 w-24 rounded-xl object-cover" loading="lazy" />

      <span className="absolute top-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {PHOTO_MOMENT_LABELS[photo.moment]}
      </span>

      <button
        type="button"
        onClick={onToggleRelease}
        aria-label={released ? 'Esconder do cliente' : 'Mostrar ao cliente'}
        className={`absolute right-1 bottom-1 flex h-8 w-8 items-center justify-center rounded-full text-[14px] ${
          released ? 'bg-primary text-primary-ink' : 'bg-black/60 text-white'
        }`}
      >
        {released ? '👁' : '🔒'}
      </button>

      <button
        type="button"
        onClick={onRemove}
        aria-label="Apagar foto"
        className="absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-[14px] text-white"
      >
        ×
      </button>
    </div>
  );
}

/** 'AAAA-MM-DD' no fuso da barbearia. */
function calendarDateOf(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(instant));
}

/** 0 = domingo … 6 = sábado, no fuso da barbearia. */
function weekdayIndexOf(instant: string, timezone: string): number {
  return new Date(`${calendarDateOf(instant, timezone)}T12:00:00Z`).getUTCDay();
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-2.5 last:border-b-0">
      <dt className="text-[13px] text-ink-faint">{label}</dt>
      <dd className="text-[14px] font-medium text-ink">{value}</dd>
    </div>
  );
}

function statusTone(status: string): 'neutral' | 'primary' | 'warn' | 'danger' | 'ok' {
  if (status === 'REQUESTED') return 'warn';
  if (status === 'IN_PROGRESS') return 'primary';
  if (status === 'COMPLETED') return 'ok';
  if (status === 'NO_SHOW' || status.startsWith('CANCELED') || status === 'REJECTED') {
    return 'danger';
  }
  return 'neutral';
}
